import { useState } from 'react'
import isUrlHttp from 'is-url-http'

import { DEFAULT_INFORMATION } from 'utils/constants'

export interface AvatarImageProps {
	src?: string | null
	className?: string
	alt?: string
	defaultImage?: string
	/** Shown when `src` fails to load (e.g. an expired TikTok CDN link), before the default image. */
	fallbackSrc?: string | null
}

export function getUrlFromAvatarImage(src?: string | null, defaultImage = DEFAULT_INFORMATION.avatarImageUrl) {
	let url = src
	if (!url) {
		url = defaultImage ?? DEFAULT_INFORMATION.avatarImageUrl
	} else {
		if (!isUrlHttp(url) && url.indexOf('data:') === -1) {
			url = `${import.meta.env.VITE_MEDIA_URL}/${src}`
		}
	}

	return url
}

export function AvatarImage({ src, defaultImage, fallbackSrc, className, ...props }: AvatarImageProps) {
	// Resolve to final URLs and drop duplicates: advancing to a URL identical to the failed one would
	// leave `img.src` unchanged, so no new load/error would fire and the default image never showed.
	const candidates = [src, fallbackSrc]
		.filter((candidate): candidate is string => Boolean(candidate?.trim()))
		.map(candidate => getUrlFromAvatarImage(candidate))
		.filter((url, index, urls) => urls.indexOf(url) === index)

	// How many candidates already failed, tied to the pair it was counted for. A new pair resets it
	// (also on a return to an earlier pair), so a transiently failed image is retried.
	const sourcesKey = candidates.join('|')
	const [failures, setFailures] = useState({ sourcesKey, count: 0 })
	if (failures.sourcesKey !== sourcesKey) setFailures({ sourcesKey, count: 0 })
	const failedCount = failures.sourcesKey === sourcesKey ? failures.count : 0

	const current = candidates[failedCount]
	// Past the last candidate, render the default image; it has no fallback left to try.
	const url = current ?? getUrlFromAvatarImage(undefined, defaultImage)
	const onError = current ? () => setFailures({ sourcesKey, count: failedCount + 1 }) : undefined

	return (
		<div className={`rounded-full bg-accent-surface overflow-hidden ${className || ''}`}>
			<img src={url} onError={onError} className="size-full object-cover" {...props} />
		</div>
	)
}
